// recordUtils.js
// Pure JS AudioRecorder utility extracted from Recorder.js - no React dependencies
// Maintains exact same function names and structure for easy migration

import { sendRecordingIndicator } from './recordingIndicator.js';

const MAX_RECORD_TIME = 2; // 2 Hours

// TODO:
// Check Rollover at 2 min
// Cap and Max Record Time
const MAX_UPLOAD_SIZE = 5 * 1024 * 1024; // 5MB in bytes (With my airpods on it came out to ~1MB per minute)
const MAX_WHISPER_UPLOAD_SIZE = 20 * 1024 * 1024;
const MIN_AUDIO_UPLOAD_DURATION_MS = 1000;

//API to handle audio recording
var blobLength = 0;
let audioQueue = []; // Queue for audio blobs

var audioRecorder = {
  /** Stores the recorded audio as Blob objects of audio data as the recording continues*/
  audioBlobs: [] /*of type Blob[]*/,
  /** Stores the reference of the MediaRecorder instance that handles the MediaStream when recording starts*/
  mediaRecorder: null /*of type MediaRecorder*/,
  /** Stores the reference to the stream currently capturing the audio*/
  streamBeingCaptured: null /*of type MediaStream*/,
  /** Start recording the audio
   * @returns {Promise} - returns a promise that resolves if audio recording successfully started
   */
  start: function (stream) {
    return new Promise((resolve, reject) => {
      try {
        //save the reference of the stream to be able to stop it when necessary
        audioRecorder.streamBeingCaptured = stream;
        //create a media recorder instance by passing that stream into the MediaRecorder constructor
        audioRecorder.mediaRecorder = new MediaRecorder(
          stream
        ); /*the MediaRecorder interface of the MediaStream Recording
        API provides functionality to easily record media*/

        //clear previously saved audio Blobs, if any
        audioRecorder.audioBlobs = [];

        //add a dataavailable event listener in order to store the audio data Blobs when recording
        audioRecorder.mediaRecorder.addEventListener(
          'dataavailable',
          (event) => {
            //store audio Blob object
            audioRecorder.audioBlobs.push(event.data);
            blobLength += 1;
          }
        );

        //start the recording by calling the start method on the media recorder
        // Crazy, but the start(1000) is necessary for apple devices for some reason...
        audioRecorder.mediaRecorder.start(1000); // This starts recording and sends out small chunks of data every second. This allows you to process or save the data continuously, and helps manage memory better during the recording.
        resolve(); // resolved when started
      } catch (error) {
        reject(error);
      }
    });
  },
  /** Stop the started audio recording
   * @returns {Promise} - returns a promise that resolves to the audio as a blob file
   */
  stop: function () {
    //return a promise that would return the blob or URL of the recording
    return new Promise((resolve) => {
      //save audio type to pass to set the Blob type
      let mimeType = audioRecorder.mediaRecorder.mimeType;

      //listen to the stop event in order to create & return a single Blob object
      audioRecorder.mediaRecorder.addEventListener('stop', () => {
        //create a single blob object, as we might have gathered a few Blob objects that needs to be joined as one
        // console.log(len(audioRecorder.audioBlobs));
        let audioBlob = new Blob(audioRecorder.audioBlobs, { type: mimeType });

        //resolve promise with the single audio blob representing the recorded audio
        resolve(audioBlob);
      });
      audioRecorder.cancel();
    });
  },
  /** Cancel audio recording*/
  cancel: function () {
    //stop the recording feature
    audioRecorder.mediaRecorder.stop();

    //stop all the tracks on the active stream in order to stop the stream
    audioRecorder.stopStream();

    //reset API properties for next recording
    audioRecorder.resetRecordingProperties();
  },
  /** Stop all the tracks on the active stream in order to stop the stream and remove
   * the red flashing dot showing in the tab
   */
  stopStream: function () {
    //stopping the capturing request by stopping all the tracks on the active stream
    if (audioRecorder.streamBeingCaptured) {
      audioRecorder.streamBeingCaptured
        .getTracks() //get all tracks from the stream
        .forEach((track) /*of type MediaStreamTrack*/ => track.stop()); //stop each one
    }

    // Clean up original streams if they exist (for combined recording)
    // But be careful not to stop the display stream if we want to reuse it
    if (audioRecorder.originalStreams) {
      audioRecorder.originalStreams.forEach((stream) => {
        // Only stop microphone streams, not display streams that we want to reuse
        const audioTracks = stream.getAudioTracks();
        const videoTracks = stream.getVideoTracks();

        // If it's a microphone-only stream (audio only, no video), stop it
        if (audioTracks.length > 0 && videoTracks.length === 0) {
          stream.getTracks().forEach((track) => track.stop());
        }
        // For display streams (have video), we'll keep them for reuse
      });
      audioRecorder.originalStreams = null;
    }

    // Clean up audio context if it exists
    if (audioRecorder.audioContext) {
      audioRecorder.audioContext.close();
      audioRecorder.audioContext = null;
    }
  },
  /** Reset all the recording properties including the media recorder and stream being captured*/
  resetRecordingProperties: function () {
    audioRecorder.mediaRecorder = null;
    audioRecorder.streamBeingCaptured = null;

    /*No need to remove event listeners attached to mediaRecorder as
        If a DOM element which is removed is reference-free (no references pointing to it), the element itself is picked
        up by the garbage collector as well as any event handlers/listeners associated with it.
        getEventListeners(audioRecorder.mediaRecorder) will return an empty array of events.*/
  }
};

// Global variables
let sendChunkIntervalID = null;
let audioRecordStartTime = 0;
let lastSendTime = 0;
let elapsedTimeTimer = null;
let stoppedDueToMutedMic = false; // Track if recording was stopped due to muted microphone
/*We aim to have notes ready within 20 seconds after stopping. Whisper takes 10-15 seconds to 
transcribe every 2 minutes of audio, plus another 10-15 seconds for AI responses, 
totaling 20-30 seconds. Shortening chunk size might compromise context accuracy. 
Our rate limit used to be 3 calls per minute but has increased to 500. With 2-minute chunks, 
we can handle up to 1000 users concurrently. Extending chunks to 4 minutes could double 
this capacity. We plan to build GPU-powered Whisper Machines for better performance and efficiency.*/

let chunkInterval = 2 * 60 * 1000; // 2 minutes
// let chunkInterval = 10 * 1000   // 10 seconds

// Helper function to detect OS and provide system settings link
function getSystemSettingsLink() {
  const platform = navigator.platform.toLowerCase();
  const userAgent = navigator.userAgent.toLowerCase();
  if (platform.includes('mac') || userAgent.includes('mac')) {
    return {
      os: 'macOS',
      link: 'x-apple.systempreferences:com.apple.preference.Sound.prefPane',
      linkText: 'Open macOS System Preferences'
    };
  } else if (platform.includes('win') || userAgent.includes('windows')) {
    return {
      os: 'Windows',
      link: 'ms-settings:privacy-microphone',
      linkText: 'Open Windows Privacy Settings'
    };
  } else {
    return {
      os: 'Unknown',
      link: null,
      linkText: 'Check your system audio settings'
    };
  }
}

export function AudioRecorder(
  conversationID,
  apiClient,
  setStatus,
  refresh,
  seconds_transcribed = 0
) {
  /*
    Parameters:
        conversationID - The conversation ID to upload audio to
        apiClient - API client for making upload calls
        setStatus - Function to call to update status messages
        refresh - Function to call to refresh the conversation/transcript
        seconds_transcribed - Number of seconds already transcribed (optional, default 0)

    returns:
        Object with methods to start recording audio and send it to the server
            Setting status along the way
            And updating the transcript when it is done
  */
  // Arguments validation
  if (conversationID == null) {
    console.error('AudioRecorder: conversationID parameter is null');
  }
  if (refresh == null) {
    console.error('AudioRecorder: refresh parameter is null');
  }
  if (setStatus == null) {
    console.error('AudioRecorder: setStatus parameter is null');
  }
  if (apiClient == null) {
    console.error('AudioRecorder: apiClient parameter is null');
  }

  // Globals
  const isUploadingQueueRef = { current: false };
  let wordChangeCount = 0;
  const setWordChangeCount = (count) => {
    wordChangeCount = count;
  };

  let isSpeaking = false;
  const setIsSpeaking = (speaking) => {
    isSpeaking = speaking;
  };
  const audioContextRef = { current: null };
  const audioAnalyserRef = { current: null }; // Single ref for audio analysis
  const speakingTimeoutRef = { current: null };

  // Conversation ID Mirror - use the passed conversationID directly
  const conversationIDRef = { current: conversationID };
  // We update the conversationIDRef whenever we click start recording, so that we can use it when we unmount or when we click new convo
  // Determine if we should show "Resume" or "Record" based on seconds transcribed in the conversation
  let hasStartedRecording = seconds_transcribed !== 0; // This is used to check if the user has started recording
  const setHasStartedRecording = (started) => {
    hasStartedRecording = started;
  };

  // View Manager
  let isRecording = false;
  const setIsRecording = (recording) => {
    isRecording = recording;
  };
  const isRecordingRef = { current: isRecording };
  // Set true when the user explicitly clicks Stop, checked in sendChunk's stop→start gap
  // to prevent a stop click that lands during chunk rollover from being ignored (ghost recording).
  const stopRequestedRef = { current: false };
  // We use a reference to the isRecording state because when the component unmounts, the real
  // isRecording will be reset to false, but we need to know if we are recording when we unmount
  // so we can stop the recording
  // Update ref whenever isRecording changes
  const updateIsRecordingRef = () => {
    isRecordingRef.current = isRecording;
  };

  let isUploading = false;
  const setIsUploading = (uploading) => {
    isUploading = uploading;
  };
  let recordingMode = 'microphone'; // 'microphone' or 'combined'
  const setRecordingMode = (mode) => {
    recordingMode = mode;
  };
  let showModal = false;
  const setShowModal = (show) => {
    showModal = show;
  };
  let lastDisplayStream = null;
  const setLastDisplayStream = (stream) => {
    lastDisplayStream = stream;
  };
  let showDropdown = false;
  const setShowDropdown = (show) => {
    showDropdown = show;
  };
  const setElapsedTime = (newTime) => {
    setStatus('Recording: ' + newTime);
  };

  const audioElementRef = { current: null };
  // const audioElementSourceRef = useRef(null);

  /** Start the audio recording*/
  function startAudioRecording() {
    try {
      // Update conversationIDRef to current conversationID
      conversationIDRef.current = conversationID;

      // Reset mute detection
      stoppedDueToMutedMic = false;

      setIsRecording(true);
      updateIsRecordingRef();

      //Feature is not supported in browser
      if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
        console.error('Browser does not support getUserMedia');
        setStatus(
          'Your browser does not support microphone access. Please try using Chrome or Firefox.'
        );
        setIsRecording(false);
        updateIsRecordingRef();
        return;
      }

      // Choose recording method based on mode
      const startMethod =
        recordingMode === 'combined'
          ? startCombinedRecordingWithState
          : startMicrophoneOnlyRecording;

      startMethod()
        .then(() => {
          if (stopRequestedRef.current) {
            if (audioRecorder.mediaRecorder) audioRecorder.cancel();
            setIsRecording(false);
            updateIsRecordingRef();
            return;
          }
          sendChunkIntervalID = setInterval(checkConditionsAndSendChunk, 1000);
          lastSendTime = new Date();
          const statusMessage =
            recordingMode === 'combined'
              ? lastDisplayStream && lastDisplayStream.active
                ? 'Recording telehealth visit...'
                : 'Recording telehealth visit...'
              : 'Recording in-clinic visit...';
          setStatus(statusMessage);
        })
        .catch((error) => {
          console.error('Recording method failed:', error);
          handleRecordingError(error);
        });
    } catch (error) {
      console.error('Exception in startAudioRecording:', error);
      handleRecordingError(error);
    }
  }

  function startMicrophoneOnlyRecording() {
    return navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        // Stop landed while we were waiting for getUserMedia — release the stream
        // before allocating AudioContext/analyser/monitorAudio loop.
        if (stopRequestedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        // Use the same stream for recording and audio visualization
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
        const microphone =
          audioContextRef.current.createMediaStreamSource(stream);
        audioAnalyserRef.current = audioContextRef.current.createAnalyser();
        microphone.connect(audioAnalyserRef.current);
        // Direct system-level mute detection using MediaStreamTrack
        const audioTrack = stream.getAudioTracks()[0];

        // Check if already muted when starting
        if (audioTrack.muted) {
          stoppedDueToMutedMic = true; // Set flag when stopped due to mute
          const systemInfo = getSystemSettingsLink();

          let statusMessage = `Your system level microphone is muted. ${systemInfo.linkText}. Open System settings > Sound > Input(Microphone) to unmute your microphone.Reload the Chrome extension.`;
          setStatus(statusMessage);
          // Automatically try to open system settings
          if (systemInfo.link) {
            try {
              window.open(systemInfo.link, '_blank');
            } catch (e) {
              // Could not auto-open system settings
            }
          }
          setIsRecording(false);
          updateIsRecordingRef();
          return Promise.reject('Microphone muted');
        }
        monitorAudio(); // Start visual/audio monitoring

        // Start MediaRecorder using same stream
        return audioRecorder.start(stream);
      });
  }

  function startCombinedRecording() {
    return audioRecorder
      .startCombined()
      .then(() => {
        // For combined recording, we still need to set up audio monitoring
        // We'll use a separate microphone stream just for monitoring
        return navigator.mediaDevices.getUserMedia({ audio: true });
      })
      .then((micStream) => {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
        const microphone =
          audioContextRef.current.createMediaStreamSource(micStream);
        audioAnalyserRef.current = audioContextRef.current.createAnalyser();
        microphone.connect(audioAnalyserRef.current);

        monitorAudio(); // Start visual/audio monitoring
      });
  }

  // Enhanced combined recording function that uses component state
  function startCombinedRecordingWithState() {
    return new Promise(async (resolve, reject) => {
      try {
        let displayStream = null;

        // Try to reuse existing display stream if it's still active
        if (
          lastDisplayStream &&
          lastDisplayStream.active &&
          lastDisplayStream.getAudioTracks().length > 0
        ) {
          displayStream = lastDisplayStream;

          // Skip to microphone setup since we're reusing the display stream
          const micStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: false,
              noiseSuppression: false,
              sampleRate: 44100,
              autoGainControl: false
            }
          });

          // Create audio context to mix both streams
          const audioContext = new (window.AudioContext ||
            window.webkitAudioContext)();
          const destination = audioContext.createMediaStreamDestination();

          // Connect system audio with gain control
          const systemSource =
            audioContext.createMediaStreamSource(displayStream);
          const systemGain = audioContext.createGain();
          systemGain.gain.value = 1.0;
          systemSource.connect(systemGain);
          systemGain.connect(destination);

          // Connect microphone audio with gain control
          const micSource = audioContext.createMediaStreamSource(micStream);
          const micGain = audioContext.createGain();
          micGain.gain.value = 1.0;
          micSource.connect(micGain);
          micGain.connect(destination);

          // Use the combined stream for recording
          const combinedStream = destination.stream;

          // Store references to original streams for cleanup
          audioRecorder.originalStreams = [displayStream, micStream];
          audioRecorder.audioContext = audioContext;

          audioRecorder.streamBeingCaptured = combinedStream;
          audioRecorder.mediaRecorder = new MediaRecorder(combinedStream, {
            mimeType: 'audio/webm;codecs=opus'
          });
          audioRecorder.audioBlobs = [];

          audioRecorder.mediaRecorder.addEventListener(
            'dataavailable',
            (event) => {
              audioRecorder.audioBlobs.push(event.data);
              blobLength += 1;
            }
          );

          audioRecorder.mediaRecorder.start(1000);

          // Set up audio monitoring with a separate microphone stream
          audioContextRef.current = new (window.AudioContext ||
            window.webkitAudioContext)();
          const monitorMicStream = await navigator.mediaDevices.getUserMedia({
            audio: true
          });
          const microphone =
            audioContextRef.current.createMediaStreamSource(monitorMicStream);
          audioAnalyserRef.current = audioContextRef.current.createAnalyser();
          microphone.connect(audioAnalyserRef.current);
          monitorAudio();

          resolve();
          return;
        }

        // Get new display stream using getDisplayMedia (reliable approach)

        const constraints = {
          video: true, // Include video to get better audio options
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            sampleRate: 44100,
            autoGainControl: false
          }
        };

        displayStream =
          await navigator.mediaDevices.getDisplayMedia(constraints);

        // Store the display stream for potential reuse
        setLastDisplayStream(displayStream);

        // Check if audio track is available
        if (!displayStream || displayStream.getAudioTracks().length === 0) {
          if (displayStream) {
            displayStream.getTracks().forEach((track) => track.stop());
          }
          setLastDisplayStream(null);
          reject(
            new Error(
              'No audio available. Please make sure your video call is active and you can hear the patient speaking.'
            )
          );
          return;
        }

        // Get microphone audio
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            sampleRate: 44100,
            autoGainControl: false
          }
        });

        // Create audio context to mix both streams
        const audioContext = new (window.AudioContext ||
          window.webkitAudioContext)();
        const destination = audioContext.createMediaStreamDestination();

        // Connect system audio with gain control
        const systemSource =
          audioContext.createMediaStreamSource(displayStream);
        const systemGain = audioContext.createGain();
        systemGain.gain.value = 1.0; // Adjust system audio volume
        systemSource.connect(systemGain);
        systemGain.connect(destination);

        // Connect microphone audio with gain control
        const micSource = audioContext.createMediaStreamSource(micStream);
        const micGain = audioContext.createGain();
        micGain.gain.value = 1.0; // Adjust microphone volume
        micSource.connect(micGain);
        micGain.connect(destination);

        // Use the combined stream for recording
        const combinedStream = destination.stream;

        // Store references to original streams for cleanup
        audioRecorder.originalStreams = [displayStream, micStream];
        audioRecorder.audioContext = audioContext;

        audioRecorder.streamBeingCaptured = combinedStream;
        audioRecorder.mediaRecorder = new MediaRecorder(combinedStream, {
          mimeType: 'audio/webm;codecs=opus'
        });
        audioRecorder.audioBlobs = [];

        audioRecorder.mediaRecorder.addEventListener(
          'dataavailable',
          (event) => {
            audioRecorder.audioBlobs.push(event.data);
            blobLength += 1;
          }
        );

        audioRecorder.mediaRecorder.start(1000);

        // Set up audio monitoring with a separate microphone stream
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
        const monitorMicStream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        const microphone =
          audioContextRef.current.createMediaStreamSource(monitorMicStream);
        audioAnalyserRef.current = audioContextRef.current.createAnalyser();
        microphone.connect(audioAnalyserRef.current);
        monitorAudio();

        resolve();
      } catch (error) {
        setLastDisplayStream(null);
        reject(error);
      }
    });
  }

  function handleRecordingError(error) {
    //on error
    if (error === 'Microphone muted') {
      // Don't override the status message we already set
      return;
    }
    //No Browser Support Error
    if (
      error.message.includes(
        'mediaDevices API or getUserMedia method is not supported in this browser.'
      )
    ) {
      console.error('To record audio, use browsers like Chrome and Firefox.');
      console.error(error.message);
    }

    //Error handling structure
    switch (error.name) {
      case 'AbortError': //error from navigator.mediaDevices.getUserMedia
        console.error('An AbortError has occured.');
        break;
      case 'NotAllowedError': //error from navigator.mediaDevices.getUserMedia
        console.error(
          'A NotAllowedError has occured. User might have denied permission.'
        );
        if (recordingMode === 'combined') {
          setStatus(
            'Setup needed: Please allow access to record telehealth visit.'
          );
        } else {
          setStatus('Please allow access to record your visit.');
        }
        break;
      case 'NotFoundError': //error from navigator.mediaDevices.getUserMedia
        console.error('A NotFoundError has occured.');
        break;
      case 'NotReadableError': //error from navigator.mediaDevices.getUserMedia
        console.error('A NotReadableError has occured.');
        break;
      case 'SecurityError': //error from navigator.mediaDevices.getUserMedia or from the MediaRecorder.start
        console.error('A SecurityError has occured.');
        break;
      case 'TypeError': //error from navigator.mediaDevices.getUserMedia
        console.error('A TypeError has occured.');
        break;
      case 'InvalidStateError': //error from the MediaRecorder.start
        console.error('An InvalidStateError has occured.');
        break;
      case 'UnknownError': //error from the MediaRecorder.start
        console.error('An UnknownError has occured.');
        break;
      default:
        console.error('An error occured with the error name ' + error.name);
    }
    stopButton();
    if (recordingMode === 'combined') {
      setStatus(
        'Setup needed: Please allow access to record telehealth visit.'
      );
    } else {
      setStatus(
        'Permission Error: Please set Microphone to "Allow" in browser settings.'
      );
    }
  }

  async function checkConditionsAndSendChunk() {
    const currentTime = new Date();
    const elapsedTime = currentTime - lastSendTime;
    const isSizeExceeded = blobLength >= MAX_WHISPER_UPLOAD_SIZE;

    if (elapsedTime >= chunkInterval || isSizeExceeded) {
      // 2 minutes or 20 MB
      clearInterval(sendChunkIntervalID);
      await sendChunk();
      lastSendTime = currentTime;
    }
  }
  /**
   * Sends the current audio chunk by stopping the recording, uploading it, and then restarting the recording.
   *
   * This function first stops the ongoing audio recording to ensure that the current audio chunk is fully captured and saved.
   * The stopAudioRecording function is asynchronous, meaning it will wait for the recording to stop completely before proceeding.
   * However, while stopAudioRecording waits for the recording to stop, it does not wait for the uploadAudio function to finish
   * before starting the next recording. As a result, startAudioRecording may be triggered while the previous audio chunk is still
   * being uploaded, demonstrating the asynchronous nature of both stopAudioRecording and uploadAudio.
   */
  async function sendChunk() {
    // We need to stop the recording, make sure it is fully read via IO then start recording again
    await stopAudioRecording();
    if (stopRequestedRef.current) return;
    startAudioRecording();
    // setTimeout(() => {
    //     startAudioRecording();
    // }, 10); // 10ms delay
  }

  let frameCount = 0; // Counter to control the frequency of checks
  let animationFrameId = null; // Store the animation frame ID

  const demonitorAudio = () => {
    // Cancel the animation frame if it exists
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    // Clear any pending speaking timeout
    if (speakingTimeoutRef.current) {
      clearTimeout(speakingTimeoutRef.current);
      speakingTimeoutRef.current = null;
    }

    // Reset states
    setIsSpeaking(false);
    setWordChangeCount(0);
    frameCount = 0;

    // Reset mute detection
    stoppedDueToMutedMic = false;
  };

  const monitorAudio = () => {
    const analyser = audioAnalyserRef.current;
    const dataArray = new Uint8Array(analyser.fftSize);

    const checkAudio = () => {
      frameCount++;
      if (frameCount % 10 === 0) {
        // Adjust this value to change the frequency (10 means every 10 frames)

        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += Math.abs(dataArray[i] - 128);
        }

        const average = sum / dataArray.length;
        const threshold = 5;
        const isSpeakingDetected = average > threshold;

        if (isSpeakingDetected) {
          setWordChangeCount(wordChangeCount + 1);

          if (speakingTimeoutRef.current) {
            clearTimeout(speakingTimeoutRef.current);
            speakingTimeoutRef.current = null;
          }

          setIsSpeaking(true);
        } else if (!speakingTimeoutRef.current) {
          // Wait 1 second before turning off
          speakingTimeoutRef.current = setTimeout(() => {
            setIsSpeaking(false);
            speakingTimeoutRef.current = null;
          }, 5000); // delay after silence
        }
      }

      animationFrameId = requestAnimationFrame(checkAudio);
    };

    checkAudio();
  };

  function stopAudioRecording() {
    const recordedDurationMs =
      lastSendTime instanceof Date ? new Date() - lastSendTime : 0;

    // Stop audio monitoring
    demonitorAudio();

    if (audioAnalyserRef.current) {
      audioAnalyserRef.current.disconnect();
      audioAnalyserRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    clearInterval(sendChunkIntervalID);

    //stop the recording using the audio recording API
    return audioRecorder
      .stop()
      .then((audioAsblob) => {
        if (
          recordedDurationMs < MIN_AUDIO_UPLOAD_DURATION_MS ||
          audioAsblob.size === 0
        ) {
          console.log(
            `Skipping too-short audio recording (${recordedDurationMs}ms, ${audioAsblob.size} bytes).`
          );
          setIsUploading(false);
          window.onbeforeunload = undefined;
          setStatus('Ready to record');
          return;
        }

        audioQueue.push({
          audioBlob: audioAsblob,
          retryCount: 0,
          durationMs: recordedDurationMs
        });
        // Play recorder audio
        // playAudio(audioAsblob);
        if (!isUploadingQueueRef.current) {
          processQueue(); // Start processing the queue if it's not already running
        }
      })
      .catch((error) => {
        setIsUploading(false);
        console.error('Error stopping audio recording:', error);
        //Error handling structure
        switch (error.name) {
          case 'InvalidStateError': //error from the MediaRecorder.stop
            console.error('An InvalidStateError has occured.');
            break;
          default:
            console.error('An error occured with the error name ' + error.name);
        }
      });
  }

  const handleCancel = () => {
    setShowModal(false);
  };

  let dingTimeout;

  function playDing() {
    if (dingTimeout) return; // Prevent further calls until the timeout is cleared

    // Set the timeout flag immediately to prevent multiple calls
    dingTimeout = true;
    setShowModal(true);
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // 880 Hz = ding sound
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Set volume

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start();

    // Stop oscillator and close context after the ding
    setTimeout(() => {
      oscillator.stop();
      audioContext.close();
    }, 200); // Stop after 200ms for a short "ding"

    // Set a timeout to allow the next ding
    setTimeout(() => {
      console.log('Ding timeout cleared, ready for next ding');
      dingTimeout = false;
    }, 60000); // 60 second cooldown
  }

  function processQueue() {
    if (isUploadingQueueRef.current) {
      console.log('Queue processing is already in progress.');
      return; // Exit if already processing the queue
    }

    isUploadingQueueRef.current = true;
    console.log('Starting queue processing...');

    async function processNext() {
      if (audioQueue.length === 0) {
        console.log('Queue is empty. Stopping queue processing.');
        isUploadingQueueRef.current = false;

        // Clear the navigation blocker since uploads are complete
        window.onbeforeunload = undefined;
        console.log('🎤 Cleared window.onbeforeunload - safe to navigate');

        if (!stoppedDueToMutedMic) {
          setStatus('Transcription Complete!');
        }
        return; // Exit if the queue is empty
      }

      const { audioBlob, retryCount = 0, durationMs } = audioQueue.shift(); // Get the next audio blob in the queue with its retry count
      console.log(
        `Processing audio blob. Queue length: ${audioQueue.length}, Retry count: ${retryCount}`
      );

      try {
        await uploadAudio(audioBlob, durationMs);
        console.log('Audio upload successful.');
        // Continue processing the queue after successful upload
        processNext();
      } catch (error) {
        console.error('Error uploading audio:', error);
        const errorMessage =
          typeof error === 'string' ? error : JSON.stringify(error);

        if (errorMessage.includes('Invalid audio format')) {
          console.error('Invalid audio format detected. Skipping item.');
          playDing();
          // Do not re-add the item to the queue
          processNext(); // Continue processing the queue
        } else if (retryCount < 3) {
          console.log(
            `Re-adding the failed item to the front of the queue. Retry attempt ${retryCount + 1}`
          );
          audioQueue.unshift({
            audioBlob,
            retryCount: retryCount + 1,
            durationMs
          }); // Re-add the failed item with incremented retry count
          // Ensure flag is reset to allow retry
          isUploadingQueueRef.current = false;

          // Check if the internet is online before retrying
          if (navigator.onLine) {
            processQueue(); // Restart processing the queue
          } else {
            console.log(
              'No internet connection. Waiting for internet to be restored.'
            );
            // Listen for the online event and retry when the connection is restored
            window.addEventListener(
              'online',
              () => {
                console.log('Internet connection restored.');
                processQueue(); // Restart processing the queue when the internet is back
              },
              { once: true }
            ); // Only listen once
          }
        } else {
          console.error('Max retry attempts reached. Skipping the item.');
          playDing();
          processNext(); // Move to the next item
        }
      }
    }

    processNext(); // Start processing the queue
  }

  async function uploadAudio(recorderAudioAsBlob, durationMs = null) {
    setStatus('Uploading Audio...');

    return new Promise((resolve, reject) => {
      //read content of files (Blobs) asynchronously
      let reader = new FileReader();
      let isLarge = recorderAudioAsBlob.size > MAX_UPLOAD_SIZE;
      //once content has been read

      reader.onload = (e) => {
        // Store the base64 URL that represents the URL of the recording audio
        let base64URL = e.target.result;

        // Transform the audio data as necessary
        let rawData = base64URL;

        // Calculate the duration of the recording
        let duration;
        if (durationMs == null) {
          const endTime = new Date();
          duration = parseInt((endTime - lastSendTime) / 1000) + 1;
          lastSendTime = endTime;
        } else {
          duration = Math.max(1, Math.ceil(durationMs / 1000));
        }
        let uploadCall = isLarge
          ? apiClient.addConversationAudioLarge(
              conversationIDRef.current,
              rawData,
              duration
            )
          : apiClient.addConversationAudio(
              conversationIDRef.current,
              rawData,
              duration
            );

        uploadCall
          .then((data) => {
            if (data['error'] != null) {
              console.log('Error uploading audio: ' + data['error']);
              console.log(rawData);
              reject(data['error']);
            } else {
              // On successful upload, refresh the UI
              console.log('Uploaded audio successfully! (Refreshing now)');
              refresh();
              setStatus('Audio Uploaded!');
              resolve();
            }
          })
          .catch((error) => {
            console.log('Error uploading audio: ' + error);
            console.log(rawData);
            reject(error); // on reject it will go to the catch block in the processQueue function
          })
          .finally(() => {
            // Set the uploading status to false, indicating the upload process is complete
            setIsUploading(false);
          });
      };

      reader.onerror = (error) => {
        console.error('Error reading blob:', error);
        reject(error); // on reject it will go to the catch block in the processQueue function
      };
      // Start reading the content of the audio blob as a base64 URL
      reader.readAsDataURL(recorderAudioAsBlob);
    });
  }

  /** Computes the elapsed recording time since the moment the function is called in the format h:m:s*/
  function setupElapsedTimeStatusPush() {
    //display inital time when recording begins
    audioRecordStartTime = new Date();
    setElapsedTime('00:00');

    //create an interval that compute & displays elapsed time, as well as, animate red dot - every second
    clearInterval(elapsedTimeTimer);
    elapsedTimeTimer = setInterval(() => {
      //compute the elapsed time every second
      setElapsedTime(computeElapsedTime(audioRecordStartTime));
    }, 1000); //every second
  }

  const setupElapsedTimeStatusPushEffect = () => {
    if (isRecording) {
      setupElapsedTimeStatusPush();
    } else {
      clearInterval(elapsedTimeTimer);
    }
  };

  const preventNavigationEffect = () => {
    // if not isRecording and not isUploading, we can allow navigation away from the page
    if (!(isRecording || isUploading)) {
      window.onbeforeunload = undefined;
    }
  };

  function startButton() {
    console.log('🎤 RECORD UTILS: startButton called');
    console.log('🎤 RECORD UTILS: Current conversationID:', conversationID);
    console.log('🎤 RECORD UTILS: Current apiClient:', !!apiClient);
    console.log('🎤 RECORD UTILS: Current recording mode:', recordingMode);

    try {
      console.log('🎤 RECORD UTILS: Setting hasStartedRecording to true...');
      setHasStartedRecording(true); // Mark that recording has started at least once
      stopRequestedRef.current = false;

      console.log('🎤 RECORD UTILS: Calling startAudioRecording...');
      startAudioRecording();

      console.log('🎤 RECORD UTILS: Setting window.onbeforeunload...');
      // Block Navigation away from the page until uploaded
      window.onbeforeunload = () => true;

      sendRecordingIndicator('RECORDING_INDICATOR_START', conversationID);

      console.log('🎤 RECORD UTILS: startButton completed successfully');
    } catch (error) {
      console.error('❌ RECORD UTILS: Error in startButton:', error);
      console.error('❌ RECORD UTILS: Error stack:', error.stack);
      throw error;
    }
  }

  function stopButton() {
    stopRequestedRef.current = true;
    setIsUploading(true);
    setIsRecording(false);
    updateIsRecordingRef();

    sendRecordingIndicator('RECORDING_INDICATOR_STOP', conversationID);

    return stopAudioRecording();
  }

  /** Computes the elapsedTime since the moment the function is called in the format mm:ss or hh:mm:ss
   * @param {String} startTime - start time to compute the elapsed time since
   * @returns {String} elapsed time in mm:ss format or hh:mm:ss format, if elapsed hours are 0.
   */
  function computeElapsedTime(startTime) {
    //record end time
    let endTime = new Date();

    //time difference in ms
    let timeDiff = endTime - startTime;

    //convert time difference from ms to seconds
    timeDiff = timeDiff / 1000;

    //extract integer seconds that dont form a minute using %
    let seconds = Math.floor(timeDiff % 60); //ignoring uncomplete seconds (floor)

    //pad seconds with a zero if neccessary
    seconds = seconds < 10 ? '0' + seconds : seconds;

    //convert time difference from seconds to minutes using %
    timeDiff = Math.floor(timeDiff / 60);

    //extract integer minutes that don't form an hour using %
    let minutes = timeDiff % 60; //no need to floor possible incomplete minutes, becase they've been handled as seconds
    minutes = minutes < 10 ? '0' + minutes : minutes;

    //convert time difference from minutes to hours
    timeDiff = Math.floor(timeDiff / 60);

    //extract integer hours that don't form a day using %
    let hours = timeDiff % 24; //no need to floor possible incomplete hours, becase they've been handled as seconds

    //convert time difference from hours to days
    timeDiff = Math.floor(timeDiff / 24);

    // the rest of timeDiff is number of days
    let days = timeDiff; //add days to hours

    let totalHours = hours + days * 24;
    totalHours = totalHours < 10 ? '0' + totalHours : totalHours;

    if (totalHours === '00') {
      return minutes + ':' + seconds;
    } else {
      return totalHours + ':' + minutes + ':' + seconds;
    }
  }

  // Handle the case where the user did not click stop but the view changed...
  function onUnmount() {
    // console.log("Unmounting AudioRecorder")
    // Stop the recording if it is still going
    // console.log("isRecording: " + isRecording)
    // console.log("isRecordingRef.current: " + isRecordingRef.current)
    if (isRecordingRef.current) {
      console.log('Stopping recording due to unmount');
      stopButton();
    }

    // Clean up all streams when component unmounts
    cleanupAllStreams();
  }

  // Component-level cleanup function
  function cleanupAllStreams() {
    audioRecorder.stopStream();

    // Stop the stored display stream
    if (lastDisplayStream) {
      lastDisplayStream.getTracks().forEach((track) => track.stop());
      setLastDisplayStream(null);
    }
  }

  function updateTelehealthStatus() {
    if (recordingMode === 'combined') {
      if (lastDisplayStream && lastDisplayStream.active) {
        setStatus(
          '💡 For telehealth visits: When you click Record, choose your call tab and allow audio sharing. ✅ Ready to record telehealth visit'
        );
      } else {
        setStatus(
          '💡 For telehealth visits: When you click Record, choose your call tab and allow audio sharing.'
        );
      }
    } else {
      // Reset to default status for in-clinic mode
      setStatus('Ready to record');
    }
  }

  // Return the exact same API as the original Recorder component
  return {
    // Core audioRecorder methods
    audioRecorder,

    // High-level methods
    startAudioRecording,
    startMicrophoneOnlyRecording,
    startCombinedRecording,
    startCombinedRecordingWithState,
    stopAudioRecording,
    sendChunk,
    processQueue,
    uploadAudio,
    checkConditionsAndSendChunk,
    handleRecordingError,
    setupElapsedTimeStatusPush,
    computeElapsedTime,
    playDing,

    // UI methods
    startButton,
    stopButton,
    handleCancel,

    // Audio monitoring
    monitorAudio,
    demonitorAudio,

    // Cleanup and lifecycle
    onUnmount,
    cleanupAllStreams,
    updateTelehealthStatus,

    // State getters
    isRecording: () => isRecording,
    isUploading: () => isUploading,
    wordChangeCount: () => wordChangeCount,
    isSpeaking: () => isSpeaking,
    hasStartedRecording: () => hasStartedRecording,
    recordingMode: () => recordingMode,
    showModal: () => showModal,
    showDropdown: () => showDropdown,

    // Upload queue monitoring
    getUploadQueueStatus: () => ({
      queueLength: audioQueue.length,
      isProcessing: isUploadingQueueRef.current,
      isComplete: audioQueue.length === 0 && !isUploadingQueueRef.current
    }),

    // State setters
    setIsRecording,
    setIsUploading,
    setWordChangeCount,
    setIsSpeaking,
    setHasStartedRecording,
    setRecordingMode,
    setShowModal,
    setShowDropdown,
    setLastDisplayStream,

    // Effects (call these manually in your usage)
    setupElapsedTimeStatusPushEffect,
    preventNavigationEffect
  };
}
